import {
  emptyValue,
  plotagemLabelById,
  refreshIntervalMs,
  refreshIntervalSeconds,
  statusPatioLabels,
  statusPatioVariants,
  positionRefreshIntervalMs
} from './constants';

export const getValueOrFallback = value => {
  if (value === null || value === undefined || value === '') return emptyValue;
  return value;
};

export const getPlotagemLabel = value => {
  if (value === null || value === undefined || value === '') return emptyValue;

  const label = plotagemLabelById[Number(value)];
  if (label) return label;

  return String(value);
};

export const formatCapacidade = value => {
  if (value === null || value === undefined || value === '') return emptyValue;
  return `${value} kg`;
};

export const formatDistanceKm = value => {
  if (value === null || value === undefined || value === '') return emptyValue;
  return `${value} km`;
};

export const formatCargaNumero = carga => getValueOrFallback(
  carga?.numeroCarga
  || carga?.numeroPreCarga
  || carga?.numeroPedidoEmbarcador
  || carga?.protocoloCarga
);

export const formatCargasResumo = (cargas = [], viagens = []) => {
  const cargaNumbers = Array.from(new Set(
    cargas
      .map(carga => String(carga?.numeroCarga || '').trim())
      .filter(Boolean)
  ));

  if (cargaNumbers.length) return cargaNumbers.join(', ');

  const viagemNumbers = Array.from(new Set(
    viagens
      .map(viagem => String(viagem?.numeroCarga || '').trim())
      .filter(Boolean)
  ));

  if (viagemNumbers.length) return viagemNumbers.join(', ');

  const fallbackNumbers = cargas
    .map(carga => String(carga?.numero || '').trim())
    .filter(Boolean);

  if (!fallbackNumbers.length) return emptyValue;

  return fallbackNumbers.join(', ');
};

export const formatViagensResumo = viagens => {
  const ids = Array.from(new Set(
    (viagens || [])
      .map(viagem => viagem?.id ?? viagem?.idViagem)
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(value => String(value))
  ));

  if (!ids.length) return emptyValue;
  return ids.join(', ');
};

export const formatStatusViagensResumo = viagens => {
  const statuses = Array.from(new Set(
    (viagens || [])
      .map(viagem => String(viagem?.statusLogistico || '').trim())
      .filter(Boolean)
  ));

  if (!statuses.length) return emptyValue;
  return statuses.join(', ');
};

export const formatViagemValuesResumo = (viagens, key) => {
  const values = Array.from(new Set(
    (viagens || [])
      .map(viagem => viagem?.[key])
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(value => String(value).trim())
      .filter(Boolean)
  ));

  if (!values.length) return emptyValue;
  return values.join(', ');
};

export const formatViagemDateResumo = (viagens, key) => {
  const values = Array.from(new Set(
    (viagens || [])
      .map(viagem => formatPopupDateTime(viagem?.[key]))
      .filter(value => value && value !== emptyValue)
  ));

  if (!values.length) return emptyValue;
  return values.join(', ');
};

export const formatDateTimeResumo = values => {
  const formattedValues = Array.from(new Set(
    (values || [])
      .map(value => formatPopupDateTime(value))
      .filter(value => value && value !== emptyValue)
  ));

  if (!formattedValues.length) return emptyValue;
  return formattedValues.join(', ');
};

export const formatCidadeEstado = (cidade, estado) => {
  const cidadeValue = String(cidade || '').trim();
  const estadoValue = String(estado || '').trim();

  if (cidadeValue && estadoValue) return `${cidadeValue} - ${estadoValue}`;
  if (cidadeValue) return cidadeValue;
  if (estadoValue) return estadoValue;
  return emptyValue;
};

export const formatEnderecoLinha = endereco => {
  if (!endereco) return emptyValue;

  const partes = [
    endereco?.logradouro,
    endereco?.numero,
    endereco?.bairro
  ]
    .map(value => String(value || '').trim())
    .filter(Boolean);

  if (!partes.length) return emptyValue;
  return partes.join(', ');
};

export const formatTiposOperacaoResumo = cargas => {
  const tipos = Array.from(new Set(
    (cargas || [])
      .map(carga => String(carga?.tipoOperacao || '').trim())
      .filter(Boolean)
  ));

  if (!tipos.length) return emptyValue;
  return tipos.join(', ');
};

export const formatDisponibilidadeTms = value => (value ? 'Disponivel' : 'Indisponivel');

export const getStatusPatioLabel = value => {
  const statusCode = Number(value);
  return statusPatioLabels[statusCode] || 'Nao informado';
};

export const getStatusPatioVariant = value => {
  const statusCode = Number(value);
  return statusPatioVariants[statusCode] || 'secondary';
};

export const getDisponibilidadeTextColor = value => (value ? '#15803d' : '#b91c1c');

export const getStatusPatioTextColor = variant => {
  if (variant === 'success') return '#15803d';
  if (variant === 'danger') return '#b91c1c';
  if (variant === 'warning') return '#b45309';
  return '#475569';
};

export const formatRefreshCountdown = seconds => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes <= 0) return `${remainingSeconds}s`;

  return `${minutes} : ${String(remainingSeconds).padStart(2, '0')}`;
};

export const formatPositionRefreshCountdown = remainingMs => {
  const totalSeconds = Math.max(0, Math.ceil((remainingMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const formatLastUpdated = value => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

export const getLastUpdatedLabel = ({ loading, lastUpdatedAt }) => {
  if (loading && !lastUpdatedAt) return 'Atualizando dados...';
  if (!lastUpdatedAt) return 'Aguardando primeira atualizacao';
  const formattedLastUpdated = formatLastUpdated(lastUpdatedAt);
  if (!formattedLastUpdated) return loading ? 'Atualizando dados...' : 'Aguardando primeira atualizacao';
  if (loading) return `Atualizando... Ultima: ${formattedLastUpdated}`;
  return `Ultima atualizacao: ${formattedLastUpdated}`;
};

export const getRemainingRefreshSeconds = lastUpdatedAt => {
  if (!lastUpdatedAt) return refreshIntervalSeconds;

  const updatedAt = new Date(lastUpdatedAt);

  if (Number.isNaN(updatedAt.getTime())) return refreshIntervalSeconds;

  const remainingMs = refreshIntervalMs - (Date.now() - updatedAt.getTime());

  if (remainingMs <= 0) return 0;

  return Math.ceil(remainingMs / 1000);
};

export const getRemainingPositionRefreshMs = lastUpdatedAt => {
  if (!lastUpdatedAt) return positionRefreshIntervalMs;

  const updatedAt = new Date(lastUpdatedAt);

  if (Number.isNaN(updatedAt.getTime())) return positionRefreshIntervalMs;

  const remainingMs = positionRefreshIntervalMs - (Date.now() - updatedAt.getTime());
  return remainingMs > 0 ? remainingMs : 0;
};

export const getPositionSourceLabel = integracao => (
  integracao?.provider ?? integracao?.Provider ?? integracao ?? '-'
);

export const formatPopupDateTime = value => {
  if (!value) return '-';

  const date = new Date(String(value).replace(' ', 'T'));

  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(date);
};

export const formatPositionDateTime = value => {
  if (!value) return '-';

  const normalizedValue = String(value).replace(' ', 'T');
  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const date = new Date(hasExplicitTimeZone ? normalizedValue : `${normalizedValue}Z`);

  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'UTC'
  }).format(date);
};
